import { useEffect, useState } from 'react';
import { getEduFieldHelp } from '@/api/edu';
import type { EduFieldHelp } from '@/api/types';
import type { FlowNode } from '@/flow-ir/types';

/**
 * Field-level help fetch + cache (F-ED-03). One request per family per session (module cache +
 * in-flight dedupe), shared across every SchemaForm rendering that family. Degrades cleanly: any
 * error (incl. a 404 when Track A has not wired the endpoint) is cached as `absent` so help icons
 * simply do not render and we never re-hammer the endpoint.
 */

export interface FamilyHelp {
  /** `available` = the endpoint responded (we can trust "missing field" = a real coverage gap). */
  status: 'available' | 'absent';
  byField: Map<string, EduFieldHelp>;
}

const cache = new Map<string, FamilyHelp>();
const inflight = new Map<string, Promise<FamilyHelp>>();

function loadFamilyHelp(family: string): Promise<FamilyHelp> {
  const cached = cache.get(family);
  if (cached) return Promise.resolve(cached);
  const pending = inflight.get(family);
  if (pending) return pending;
  const p = getEduFieldHelp(family)
    .then((fields) => {
      const rec: FamilyHelp = { status: 'available', byField: new Map() };
      for (const f of fields) rec.byField.set(f.field, f);
      cache.set(family, rec);
      inflight.delete(family);
      return rec;
    })
    .catch(() => {
      const rec: FamilyHelp = { status: 'absent', byField: new Map() };
      cache.set(family, rec);
      inflight.delete(family);
      return rec;
    });
  inflight.set(family, p);
  return p;
}

export interface FieldHelpApi {
  /** Resolve help for a field key; falls back to the dotted-path leaf (e.g. `config.threshold`→`threshold`). */
  lookup: (field: string) => EduFieldHelp | undefined;
  /** True once the family's help set has loaded from a responding endpoint. */
  available: boolean;
  loading: boolean;
}

const EMPTY: FieldHelpApi = { lookup: () => undefined, available: false, loading: false };

function leafOf(field: string): string {
  const i = field.lastIndexOf('.');
  return i >= 0 ? field.slice(i + 1) : field;
}

export function useFieldHelp(family?: string): FieldHelpApi {
  const [rec, setRec] = useState<FamilyHelp | null>(() => (family ? cache.get(family) ?? null : null));
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!family) {
      setRec(null);
      return;
    }
    const cached = cache.get(family);
    if (cached) {
      setRec(cached);
      return;
    }
    let cancelled = false;
    setLoading(true);
    loadFamilyHelp(family).then((r) => {
      if (!cancelled) {
        setRec(r);
        setLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [family]);

  if (!family) return EMPTY;
  return {
    available: rec?.status === 'available',
    loading,
    lookup: (field: string) => rec?.byField.get(field) ?? rec?.byField.get(leafOf(field)),
  };
}

/** The capability family for a node's help lookup — `"screening.v1"` → `"screening"`. */
export function capabilityFamily(node: FlowNode): string | undefined {
  if (node.kind !== 'capability' || !node.capability) return undefined;
  return node.capability.replace(/\.v\d+$/, '') || node.capability;
}

/** The help `family` for any node: the capability family, else the node kind (structured fields). */
export function familyForNode(node: FlowNode): string {
  return capabilityFamily(node) ?? node.kind;
}
